import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import {
  Component,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from 'angular-ts-decorators';
import { reverse, values, includes, flow } from 'lodash';

import { DateSource } from '@shared/app/scripts/services/date-source';
import { PeriodType } from '@core/app/scripts/services/period-type';

/**
 * Let the user select a time period (a day/month/...)
 *
 * ## Attributes
 * * period - The type of period to select; one of day,
 *   week, month, quarter or year
 * * date - Date at the beginning of the period. This is a
 *   Date object in the scope.
 *   This is initially red as default value for the period
 *   and updated when the user changes the period.
 *   Normally this should really be the beginning of
 *   a period (for month for instance the first of April at
 *   00:00), but you can enter values between periods (e.g.
 *   a Tuesday in a week); in those cases we will try to
 *   really choose sensible dates, when the value changes
 *   (subtracting one week from a Tuesday should yield
 *   a Tuesday seven days earlier).
 *   There are some edge cases though in which your value
 *   would be truncated (going back a month from the 31st of
 *   March would yield the 31st of February. However, since
 *   that date does not exist, the date would be truncated)
 *   * update – Called when date changes
 *
 * ## Example
 * <code><pre>
 *   <period-picker
 *     date='dateVariable'
 *     update='test()'
 *     period="quarter"></period-picker>
 * </pre></code>
 */
@Component({
  selector: 'periodPicker',
  template: require('./period-picker.html').default,
})
export class PeriodPickerComponent implements OnInit, OnDestroy {
  @Input('@') period: string;
  @Input() date: string;
  @Output() update: (any?) => any;

  updateTimeout: any;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  dateOptions: any[];

  constructor(
    private $scope: ng.IScope,
    private $timeout: ng.ITimeoutService,
    private dateSource: DateSource,
    private moment,
    private periodType: PeriodType,
    private timeZone,
  ) {
    'ngInject';

    this.$scope.$watch('$ctrl.period', () => {
      this.updateDateOptions();
    });
  }

  private isValidPeriod(period: string): boolean {
    return flow(values, (list) => includes(list, period))(this.periodType);
  }

  private updateDateOptions(): void {
    this.period = this.isValidPeriod(this.period) ? this.period : 'day';

    const pickedMoment = this.moment(
        angular.isDate(this.date) ? this.date : this.dateSource.now(),
      ),
      nowMoment = this.moment(this.dateSource.now());

    const rangeStartMoment = this.moment(
        pickedMoment.isBefore(nowMoment) ? pickedMoment : nowMoment,
      ),
      rangeEndMoment = this.moment(
        pickedMoment.isAfter(nowMoment) ? pickedMoment : nowMoment,
      );

    switch (this.period) {
      case 'day':
        return;
      case 'week':
      case 'month':
        rangeStartMoment.startOf('year');
        rangeStartMoment.subtract(2, 'year');
        break;
      case 'quarter':
      case 'year':
        // start of spynl epoch
        rangeStartMoment.year(2014);
        break;
    }

    if (this.period === 'month') {
      rangeStartMoment.set(0, this.period);
    } else {
      rangeStartMoment.set(1, this.period);
    }

    rangeStartMoment.startOf(this.period);

    let coursorMoment = this.moment(pickedMoment),
      dateOptions = [coursorMoment.toDate()];

    coursorMoment.add(1, this.period);

    // add dates from after the current date, until the end
    while (coursorMoment.isSameOrBefore(rangeEndMoment)) {
      dateOptions.push(coursorMoment.toDate());
      coursorMoment.add(1, this.period);
    }

    dateOptions = reverse(dateOptions);

    coursorMoment = this.moment(pickedMoment).subtract(1, this.period);

    // add dates from before the current date, until the start
    while (coursorMoment.isSameOrAfter(rangeStartMoment)) {
      dateOptions.push(coursorMoment.toDate());
      coursorMoment.subtract(1, this.period);
    }

    this.dateOptions = dateOptions;
  }

  // moves the current date forward or backwards in the time of a given period
  private movePeriod(action: string, period: string) {
    //TODO throw exception in case of non valid this.period value
    const momentDate = this.moment(this.date);
    this.date = momentDate[action](1, period).toDate();

    this.updateDateOptions();

    this.$timeout(() => {
      this.updateTimeout = this.update({
        date: this.date,
        period: this.period,
      });
    });
  }

  /**
   *
   * Move date to the next period
   * @return {Void}
   * Nothing
   */
  nextPeriod(): void {
    this.movePeriod('add', this.period);
  }

  /**
   *
   * Move date to the previous period
   * @return {Void}
   * Nothing
   */
  previousPeriod() {
    this.movePeriod('subtract', this.period);
  }

  /**
   * Notifies watchers about change on the model
   * @return {Void}
   * Nothing
   */
  // TODO move to RxJs
  onChange() {
    this.updateDateOptions();

    this.updateTimeout = this.$timeout(() => {
      this.update({ date: this.date, period: this.period });
    });
  }

  ngOnInit() {
    this.update = angular.isFunction(this.update) ? this.update : angular.noop;

    this.updateDateOptions();
  }

  ngOnDestroy(): boolean {
    return this.$timeout.cancel(this.updateTimeout);
  }
}
